import { useEffect, useState, useMemo } from 'react';
import { notification } from 'antd';
import { Footer } from '../../components';
import CopyOutlined from '@ant-design/icons/CopyOutlined';
import { firebaseApi } from '../../services/Firebase/firebase';
import { PropertyItem } from '../../services/realtime-db/users/users.types';
import { PropertyNames } from '../../common/constants';
import { propertyFormat } from '../../utils/properties';
import Form from './frames/form/form.frame';
import Modal from './frames/modal/modal.frame';
import cloudFunctionsInstance from '../../services/CloudFunctionsService';
import { delay } from '../../utils/helpers';
import { withAuth } from '../../features/hoc-with-auth';
import Loading from '../../components/loading/loading.component';
import RouterUrlHelper from '../../routes/RouterUrlHelper';

// hooks
import useClipboard from '../../hooks/useClipboard';

export interface IAccount {
  [accountId: string]: PropertyItem[];
}
export type TAccounts = IAccount | null;

export type TModals = 'add' | 'edit' | '';

const Accounts = () => {
  /**
   * Auth protection
   */
  const { isLoading, tgUser } = withAuth();

  const [accounts, setAccounts] = useState<TAccounts>(null);
  const [updateStatus, setUpdateStatus] = useState<'loading' | '' | 'success'>('');
  const [copyBtnIdx, setCopyBtnIdx] = useState(null);
  const { setToClipboard } = useClipboard();

  const [editId, setEditId] = useState('');
  const [modalView, setModalView] = useState<TModals>('');

  const USER_ID = tgUser ? `${tgUser.id}` : '';

  const accountsList: [accountId: string, properties: PropertyItem[]][] = useMemo(() => {
    if (!accounts) return [];
    return Object.entries(accounts);
  }, [accounts, isLoading]);

  useEffect(() => {
    if (isLoading) return;
    firebaseApi.user.getUserAccounts(USER_ID).then((r) => {
      setAccounts(r);
    });
  }, [isLoading]);

  const onSubmitEdit = async (values: PropertyItem[]) => {
    setUpdateStatus('loading');
    await cloudFunctionsInstance.property.update(USER_ID, editId, values);
    setUpdateStatus('success');
    await delay(500);
    setUpdateStatus('');
    setAccounts({ ...accounts, [editId]: values });
    setEditId('');
    setModalView('');
    document.body.classList.remove('lock');
  };

  const onPressEdit = (id: string) => {
    setModalView('edit');
    setEditId(id);
    document.body.classList.add('lock');
  };

  const onPressAdd = () => {
    setModalView('add');
    document.body.classList.add('lock');
  };

  const onSubmitAdd = async (values: PropertyItem[]) => {
    setUpdateStatus('loading');
    const resp: { status: string; key: string } = await cloudFunctionsInstance.property.add(
      USER_ID,
      values,
    );
    setUpdateStatus('success');
    await delay(500);
    setUpdateStatus('');
    setModalView('');
    setAccounts({ ...accounts, [resp.key]: values });
    document.body.classList.remove('lock');
  };

  const onAccountRemove = async () => {
    setUpdateStatus('loading');
    const resp: { status: string } = await cloudFunctionsInstance.account.remove(USER_ID, editId);
    setUpdateStatus('success');
    await delay(500);

    const newAccounts = { ...accounts };

    setModalView('');
    setEditId('');
    setUpdateStatus('');
    document.body.classList.remove('lock');

    newAccounts && delete newAccounts[editId];
    setAccounts(newAccounts);
  };
  const onModalClose = () => {
    setModalView('');
    setEditId('');
    setUpdateStatus('');
    document.body.classList.remove('lock');
  };

  const onLinkBtnClick = async (link: string, index: number) => {
    await setToClipboard(link);
    notification.open({ message: 'Скопійовано' });
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className={`${editId ? 'relative' : ''} min-h-screen flex flex-col justify-between`}>
      <div className={'relative'}>
        <div
          className={
            'vector absolute bottom-0 left-0 z-[-1] rotate-0 scale-[1.5] translate-x-[-55%]'
          }
        >
          <img src="/vector-full.svg" alt="vector" />
        </div>
        <div className="container p-4 pb-[40px]">
          <header>
            <div className={'flex flex-col relative justify-center items-center'}>
              <div className={'sm:absolute mt-12 sm:mt-0 top-[50%] left-0 -translate-y-[50%]'}>
                <a href={RouterUrlHelper.urlMap.root.path}>
                  <img src="/PayLinker.png" alt="logo" className={'w-[120px]'} />
                </a>
              </div>
              <div>
                <ul className={'flex py-8'}>
                  <li className={'mx-4'}>
                    <a href={RouterUrlHelper.urlMap.root.path} className={'text-gray-950'}>
                      На головну
                    </a>
                  </li>
                  <li className={'mx-4'}>
                    <button
                      className={'text-gray-950'}
                      onClick={() => {
                        window.localStorage.removeItem('tgUser');
                        window.location.href = RouterUrlHelper.urlMap.root.path;
                      }}
                    >
                      Вийти
                    </button>
                  </li>
                  {/*<li className={'mx-4'}>*/}
                  {/*  <a href="#" className={'text-gray-950'}>*/}
                  {/*    Посилання*/}
                  {/*  </a>*/}
                  {/*</li>*/}
                  {/*<li className={'mx-4'}>*/}
                  {/*  <a href="#" className={'text-gray-950'}>*/}
                  {/*    Рахунки*/}
                  {/*  </a>*/}
                  {/*</li>*/}
                </ul>
              </div>
            </div>
          </header>
          <section>
            <h3 className={'text-blue-dark font-bold text-[24px] sm:text-[48px] mb-[60px]'}>
              Мої рахунки
            </h3>

            <div>
              {accountsList ? (
                accountsList.map(([accountId, properties], index) => {
                  const link = `https://share-card-bot.web.app/${USER_ID}/${accountId}`;

                  return (
                    <div className={'account mb-[80px]'} key={accountId}>
                      <div
                        className={
                          'flex flex-col sm:flex-row items-center justify-between mb-[28px]'
                        }
                      >
                        <h4 className="account-title text-blue-dark font-bold text-[24px] sm:text-[32px] mb-6 sm:mb-0">
                          Рахунок {++index}
                        </h4>
                        <div
                          className={
                            'flex justify-between items-stretch bg-gray-400 rounded-md overflow-hidden hover:cursor-pointer'
                          }
                        >
                          {/*<img src="/icon-copy.svg" alt="" />*/}
                          <a
                            href={link}
                            className={
                              'text-primary-100 text-[14px] sm:text-[16px] py-[16px] px-[24px]'
                            }
                            target={'_blank'}
                          >
                            {link}
                          </a>
                          <button
                            onClick={() => onLinkBtnClick(link, index)}
                            className={'bg-primary-main py-[16px] px-[24px]'}
                          >
                            <CopyOutlined className={'text-[20px] text-white'} />
                          </button>
                        </div>
                      </div>
                      <div className={'properties'}>
                        {properties.map((property) => {
                          return (
                            <div className="property-item mb-[32px]">
                              <h3 className={'text-gray-600 text-[16px] sm:text-[26px] mb-[12px]'}>
                                {PropertyNames[property.type]}
                              </h3>
                              <div
                                className={
                                  'text-gray-600 font-bold text-[16px] text-wrap sm:text-[30px] truncate'
                                }
                              >
                                {propertyFormat(property.type, property.value)}
                              </div>
                              <div className={'text-gray-950 text-[14px] sm:text-[20px]'}>
                                {property.note || ''}
                              </div>
                            </div>
                          );
                        })}

                        <button
                          onClick={() => onPressEdit(accountId)}
                          className={
                            'bg-primary-main text-white text-[20px] w-[240px] py-[9px] rounded-md'
                          }
                        >
                          Редагувати рахунок
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <h4 className="account-title text-blue-dark font-bold text-[32px] mb-[32px]">
                  Наразі немає рахунків.
                  <br />
                  Спробуйте додати перший
                </h4>
              )}

              <div className={'mb-[40px]'}>
                <button
                  onClick={onPressAdd}
                  className={
                    'flex border-2 border-primary-main text-gray-600 text-[20px] w-[240px] py-[9px] rounded-md justify-center items-center'
                  }
                >
                  <div className={'text-[20px] text-gray-600 mr-4'}>Додати рахунок</div>
                  <img src="/icon-plus.svg" alt="" />
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
      <Footer />

      <Modal isOpen={!!editId && !!accounts} onClose={onModalClose}>
        <h3 className="account-title text-blue-dark font-bold  text-[24px] sm:text-[32px] mt-8 sm:mt-0 mb-[60px]">
          Редагувати рахунок
        </h3>
        <div className="properties">
          <div className="property-item mb-[32px]">
            <Form
              mode={modalView}
              status={updateStatus}
              // @ts-ignore
              properties={!!accounts ? accounts[editId] : []}
              onSubmit={onSubmitEdit}
              onAccountRemove={onAccountRemove}
            />
          </div>
        </div>
      </Modal>

      <Modal isOpen={modalView === 'add'} onClose={onModalClose}>
        <h3 className="account-title text-blue-dark font-bold text-[32px] mb-[60px]">
          Додати рахунок
        </h3>
        <div className="properties">
          <div className="property-item mb-[32px]">
            <Form status={updateStatus} mode={modalView} properties={[]} onSubmit={onSubmitAdd} />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Accounts;
